import { ReactNode } from "react";
import { Icon } from "@iconify/react";
import { card, colors, layout, type } from "../styles";

/** A titled band of the page — "Monetization", "Channel", etc. */
export const Section = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) => (
  <section style={{ marginBottom: 32 }}>
    <div style={{ marginBottom: 12 }}>
      <div style={{ ...type.sectionTitle, color: colors.ink }}>{title}</div>
      {description && (
        <div style={{ ...type.bodySmallRegular, color: colors.inkFaint, marginTop: 3 }}>
          {description}
        </div>
      )}
    </div>
    {children}
  </section>
);

/** Responsive auto-fit grid — the KPI row and the chart row share one rhythm. */
export const CardGrid = ({
  children,
  min = 220,
}: {
  children: ReactNode;
  min?: number;
}) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
      gap: layout.gridGap,
      alignItems: "stretch",
    }}
  >
    {children}
  </div>
);

/** Non-metric informational card. Never styled as a status/alert. */
export const NoteCard = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <div
    style={{
      ...card,
      background: colors.surfaceMuted,
      padding: 14,
      display: "flex",
      gap: 10,
      alignItems: "flex-start",
    }}
  >
    <Icon
      icon="mdi:information-outline"
      height={17}
      style={{ color: colors.inkFaint, flexShrink: 0, marginTop: 1 }}
    />
    <div>
      <div style={{ ...type.bodySmallMedium, color: colors.ink, marginBottom: 3 }}>
        {title}
      </div>
      <div style={{ ...type.bodySmallRegular, color: colors.inkMuted, lineHeight: 1.5 }}>
        {children}
      </div>
    </div>
  </div>
);

export const ErrorBanner = ({ message }: { message: string }) => (
  <div
    style={{
      ...type.bodyMedium,
      color: colors.stateFailed,
      background: colors.stateFailedBg,
      border: `1px solid ${colors.stateFailedBg}`,
      borderRadius: 8,
      padding: "10px 14px",
      marginBottom: 16,
      display: "flex",
      alignItems: "center",
      gap: 8,
    }}
  >
    <Icon icon="ion:alert-circle" height={16} />
    {message}
  </div>
);

export const LoadingNote = () => (
  <div style={{ ...type.bodyRegular, color: colors.inkMuted, padding: "24px 0" }}>
    Loading…
  </div>
);

/** Fact list used across the detail page's sections. */
export const FactList = ({
  facts,
}: {
  facts: { label: string; value: ReactNode; title?: string }[];
}) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
      gap: 16,
    }}
  >
    {facts.map(fact => (
      <div key={fact.label} style={{ minWidth: 0 }}>
        <div style={{ ...type.bodySmallMedium, color: colors.inkFaint, marginBottom: 3 }}>
          {fact.label}
        </div>
        <div
          style={{ ...type.bodyRegular, color: colors.ink, overflowWrap: "anywhere" }}
          title={fact.title}
        >
          {fact.value}
        </div>
      </div>
    ))}
  </div>
);
